import React, { useMemo } from 'react';
import { uniqBy } from 'lodash';
import { addDays } from 'date-fns';
import { useSearchOfferings } from '@services/offering';
import { useProductOfferings } from '@services/productOffering/useProductOfferings';
import { useStore } from '@store/GlobalStore';
import { getEarliestEffectiveOn, getLatestEffectiveThrough } from '@utils/brokerages';
import { TypeAheadFilter } from './TypeAheadFilter';
import { TableFilterTypeAheadFilterExtendedProps } from '@components/table/filters/TypeaheadFilterProps.type';
import { shallow } from 'zustand/shallow';

export interface OfferingTypeaheadFilterProps extends TableFilterTypeAheadFilterExtendedProps {}

export const OfferingTypeaheadFilter = (componentProps: OfferingTypeaheadFilterProps) => {
  const { value, disabled, childrenFields, selectedValues, fieldConfig } = componentProps;

  const useInsuranceProductId = fieldConfig?.offeringTypeAheadFilter?.type === 'insuranceProduct';

  const [currentUser, { isInternalUser }] = useStore((state) => [state.currentUser, state.userAuth], shallow);

  const { data: offeringList } = useSearchOfferings({ pageLimit: 100 }, isInternalUser);

  const { productOfferings } = useProductOfferings({
    enabled: true,
    brokerageId: currentUser?.brokerageUsers[0]?.brokerageId ?? '',
    includeInherited: true,
  });

  const uniqueOfferings = productOfferings?.items.length
    ? uniqBy(productOfferings.items, (brokerageOffering) => brokerageOffering.id).map((p) => ({
        ...p,
        brokerageOfferingAvailability: {
          jurisdictions: p.brokerageOfferingAvailability.jurisdictions.map((j) => ({
            ...j,
            effectiveOn: new Date(j.effectiveOn),
            effectiveThrough: new Date(j.effectiveThrough),
          })),
        },
      }))
    : [];

  const disableIfChildren = childrenFields?.some((child: string) => {
    if (selectedValues?.[child]) {
      return true;
    }
  });

  const effectiveProducts = uniqueOfferings.filter((item) => {
    const effectiveOn = getEarliestEffectiveOn([...item.brokerageOfferingAvailability.jurisdictions]);
    const effectiveThrough = getLatestEffectiveThrough([...item.brokerageOfferingAvailability.jurisdictions]);
    if (effectiveThrough) {
      return effectiveOn <= addDays(new Date(), 120) && effectiveThrough > new Date(Date.now());
    }
    return effectiveOn <= addDays(new Date(), 120);
  });

  const items = useMemo(() => {
    return isInternalUser
      ? offeringList?.items
          .map((ip) => ({
            value: useInsuranceProductId ? ip.insuranceProduct.id : ip.id,
            label: ip.label ?? ip.insuranceProduct.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : effectiveProducts
          .sort((a, b) => (a.label ?? a.insuranceProduct.name).localeCompare(b.label ?? b.insuranceProduct.name))
          .map((item) => ({
            value: useInsuranceProductId ? item.insuranceProduct.id : item.id,
            label: item.label ?? item.insuranceProduct.name,
          }));
  }, [effectiveProducts, useInsuranceProductId, isInternalUser, offeringList?.items]);

  return (
    <TypeAheadFilter
      value={value}
      defaultValue={value}
      disabled={disabled || disableIfChildren}
      label={componentProps.label}
      options={items ?? []}
      isMultiple={componentProps?.fieldConfig?.selectField?.multiSelect}
      limitTags={1}
      onChange={componentProps.onChange}
      helperText={disableIfChildren ? `Multiselect disabled due to dependent value selection` : undefined}
    />
  );
};
